import { Directive, ElementRef, EventEmitter, HostListener, Output } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appOnlyNumber]'
})
export class OnlyNumberDirective {
  constructor(private ngControl: NgControl) {}

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    // Allow only digits and a single decimal point
    let filtered = input.value.replace(/[^\d.]/g, '');
    // Prevent more than one decimal point
    const parts = filtered.split('.');
    if (parts.length > 2) {
      filtered = parts[0] + '.' + parts.slice(1).join('');
    }
    if (input.value !== filtered) {
      input.value = filtered;
      this.ngControl.control?.setValue(filtered);
    }
  }
}

/**
 * Directive to allow numeric input with up to two decimal places.
 * Usage: add attribute `appTwoDecimal` to an input (e.g. <input appTwoDecimal>)
 */
@Directive({
  selector: '[appTwoDecimal]'
})
export class TwoDecimalDirective {
 constructor(private ngControl: NgControl) {}

@HostListener('input', ['$event'])
onInput(event: Event) {
  const input = event.target as HTMLInputElement;
  
  // Remove any character that's not digit or dot
  let val = input.value.replace(/[^\d.]/g, '');
  
  // If multiple dots, keep the first one only
  const parts = val.split('.');
  let intPart = parts.length > 0 ? parts[0] : '';
  let decPart = parts.length > 1 ? parts.slice(1).join('') : null;

  // Limit integer part to 8 digits
  if (intPart.length > 8) {
    intPart = intPart.substring(0, 8);
  }

  // Limit decimal part to 2 digits (only if a dot was present)
  if (decPart !== null) {
    decPart = decPart.substring(0, 2);
    val = intPart + '.' + decPart;
  } else {
    val = intPart;
  }

  // Prevent leading zeros like 00 => 0 (but allow "0." for decimals)
  if (/^0\d+/.test(val) && !/^0\./.test(val)) {
    val = String(Number(intPart)) + (decPart !== null ? '.' + decPart : '');
  }

  if (input.value !== val) {
    input.value = val;
    this.ngControl.control?.setValue(val);
  }
}
}
@Directive({
  selector: '[appClickOutside]'
})
export class ClickOutsideDirective {
  @Output() clickOutside = new EventEmitter<void>();

  constructor(private elementRef: ElementRef) {}

  @HostListener('document:click', ['$event.target'])
  public onClick(target: HTMLElement): void {
    if (!this.elementRef.nativeElement.contains(target)) {
      this.clickOutside.emit();
    }
  }
}
@Directive({
  selector: '[decimalOnly]'
})
export class DecimalOnlyDirective {

  private previousValue: string = '';

  constructor(private el: ElementRef) {}

  @HostListener('input', ['$event'])
  onInputChange(event: any) {
    const inputEl = this.el.nativeElement;
    let value = inputEl.value as string;

    // Allow only digits and one decimal point
    value = value.replace(/[^0-9.]/g, '');

    // Prevent multiple decimal points
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit to 2 decimal places
    const updatedParts = value.split('.');
    if (updatedParts[1] && updatedParts[1].length > 2) {
      value = updatedParts[0] + '.' + updatedParts[1].substring(0, 2);
    }

    // Validate against max value (must be < 100, i.e. max 99.99)
    // Allow intermediate typing states like "9", "99.", "9.5" etc.
    const isValidFormat = /^\d{0,2}(\.\d{0,2})?$/.test(value);
    const numericValue = parseFloat(value);

    if (isValidFormat && (value === '' || value === '.' || isNaN(numericValue) || numericValue <= 99.99)) {
      this.previousValue = value;
      inputEl.value = value;
    } else {
      // Revert to last valid value instead of blindly trimming
      inputEl.value = this.previousValue;
    }
  }
}
export const COMMON_DIRECTIVES = [DecimalOnlyDirective,ClickOutsideDirective,OnlyNumberDirective, TwoDecimalDirective];

// You can add more common directives here for easy maintenance and usage
